import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Car, CornerDownLeft, Search, SearchX, Truck, Users } from 'lucide-react';
import { ADMIN_NAV_LINKS, filterNavigation, visibleNavLinks } from '../navigation';
import { searchAdminData } from '../services/search';
import { Empty, ErrorBox, Skeleton } from '../ui/kit';

// ── Globale Command-Palette (⌘K) — von jedem Screen aus, unabhängig von der Sidebar. ──
// Inhalt: (a) Bereiche (bestehende Nav-Filterlogik inkl. Rechte-Filter), (b) ab 2 Zeichen
// debounced Datentreffer von GET /admin/search (gruppiert), (c) Aktionszeile „In Fahrten
// suchen". Deep-Links öffnen die vorhandenen Detail-Ansichten über URL-Parameter.

const GROUP_ICONS = { customers: Users, drivers: Truck, partners: Building2, vehicles: Car };

// Stöbern-Einträge (Owner-Entscheid 26.08.: alles sehen/durchsuchen/finden OHNE Tipparbeit):
// ⌘K → Pfeil → Enter springt direkt in den jeweiligen Verzeichnis-Browse-Modus.
// ?browse=1 lesen die Zielseiten beim Mount UND bei Same-Page-Navigation (wie ?detail=).
const BROWSE_LINKS = [
  { to: '/users?browse=1', label: 'Alle Kunden anzeigen', icon: Users },
  { to: '/drivers?browse=1', label: 'Alle Fahrer anzeigen', icon: Truck },
  { to: '/partners?browse=1', label: 'Alle Partner anzeigen', icon: Building2 },
  { to: '/vehicles-admin?browse=1', label: 'Alle Fahrzeuge anzeigen', icon: Car },
];

// Deep-Link je Treffer-Typ (Gruppen-Key als Rückfallebene, falls item.type fehlt).
const dataTarget = (groupKey, item) => {
  const type = String(item.type || groupKey || '');
  if (/^customers?$/.test(type)) return `/users?detail=${item.id}`;
  if (/^drivers?$/.test(type)) return `/drivers?detail=${item.id}`;
  if (/^partners?$/.test(type)) return `/partners?detail=${item.id}`;
  if (/^vehicles?$/.test(type)) return `/vehicles-admin?search=${encodeURIComponent(item.title || '')}`;
  return null;
};

export default function CommandPalette({ open, onClose, can }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const seqRef = useRef(0);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const [data, setData] = useState({ status: 'idle', groups: [] });
  const trimmed = query.trim();

  // Öffnen: Zustand zurücksetzen, Fokus ins Eingabefeld; Schließen: Fokus kehrt zurück.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActive(0);
    setData({ status: 'idle', groups: [] });
    const previouslyFocused = document.activeElement;
    window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      seqRef.current += 1;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [open]);

  // Datensuche: ab 2 Zeichen, ~300 ms debounced, Antworten außer der Reihe werden verworfen.
  useEffect(() => {
    if (!open) return undefined;
    if (trimmed.length < 2) {
      setData({ status: 'idle', groups: [] });
      return undefined;
    }
    const seq = ++seqRef.current;
    setData((d) => ({ status: 'loading', groups: d.groups }));
    const timer = window.setTimeout(() => {
      searchAdminData(trimmed)
        .then((result) => { if (seq === seqRef.current) setData({ status: 'ready', groups: result.groups }); })
        .catch(() => { if (seq === seqRef.current) setData({ status: 'error', groups: [] }); });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, trimmed, retryTick]);

  // Bereiche: dieselbe Filterlogik wie die Sidebar-Suche, inkl. Rechte-Filter.
  const navRows = useMemo(
    () => filterNavigation(visibleNavLinks(ADMIN_NAV_LINKS, can), query),
    [can, query],
  );

  // Flache Zeilenliste (für Pfeiltasten über ALLE Zeilen) mit Abschnitts-Beschriftung.
  const rows = useMemo(() => {
    const out = [];
    // Leerzustand (< 2 Zeichen): Stöbern-Gruppe zuerst — Enter auf Zeile 1 zeigt sofort alles.
    if (trimmed.length < 2) {
      BROWSE_LINKS.forEach((link) => out.push({
        kind: 'browse', section: 'Stöbern', key: `browse:${link.to}`,
        icon: link.icon, title: link.label, to: link.to,
      }));
    }
    navRows.forEach((link) => out.push({
      kind: 'nav', section: 'Bereiche', key: `nav:${link.to}`,
      icon: link.icon, title: link.label, to: link.to,
    }));
    if (data.status !== 'error') {
      data.groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          const to = dataTarget(group.key, item);
          if (!to) return;
          out.push({
            kind: 'data', section: group.label || group.key, key: `data:${group.key}:${item.id}`,
            icon: GROUP_ICONS[group.key] || Search, title: item.title,
            subtitle: item.subtitle, status: item.status, to,
          });
        });
      });
    }
    if (trimmed) {
      out.push({
        kind: 'action', section: 'Aktionen', key: 'action:rides',
        icon: CornerDownLeft, title: `In Fahrten suchen: „${trimmed}“`,
        to: `/rides?search=${encodeURIComponent(trimmed)}`,
      });
    }
    return out;
  }, [navRows, data.status, data.groups, trimmed]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => { setActive((i) => Math.max(0, Math.min(i, Math.max(rows.length - 1, 0)))); }, [rows.length]);
  useEffect(() => {
    const el = document.getElementById(`vg-cmdk-row-${active}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const activate = useCallback((row) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  }, [navigate, onClose]);

  // Fokus-Falle: Tab bleibt im Modal (Eingabefeld), Esc schließt, Pfeile + Enter steuern die Liste.
  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key === 'Tab') { event.preventDefault(); inputRef.current?.focus(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!rows.length) return;
      setActive((i) => (event.key === 'ArrowDown' ? (i + 1) % rows.length : (i - 1 + rows.length) % rows.length));
      return;
    }
    if (event.key === 'Enter') { event.preventDefault(); activate(rows[active]); }
  };

  if (!open) return null;

  const showEmpty = rows.filter((r) => r.kind !== 'action').length === 0 && data.status !== 'loading' && data.status !== 'error';
  let lastSection = null;

  return (
    <div className="vg-cmdk-overlay" onMouseDown={onClose} onKeyDown={onKeyDown}>
      <div className="vg-cmdk" role="dialog" aria-modal="true" aria-label="Globale Suche" onMouseDown={(e) => e.stopPropagation()}>
        <div className="vg-cmdk-head">
          <Search size={17} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Bereiche, Kunden, Fahrer, Partner, Fahrzeuge …"
            aria-label="Global suchen"
            role="combobox"
            aria-expanded="true"
            aria-controls="vg-cmdk-list"
            aria-activedescendant={rows.length ? `vg-cmdk-row-${active}` : undefined}
          />
          <kbd>esc</kbd>
        </div>
        <div className="vg-cmdk-list" id="vg-cmdk-list" role="listbox" aria-label="Suchergebnisse">
          {rows.map((row, index) => {
            const header = row.section !== lastSection ? row.section : null;
            lastSection = row.section;
            return (
              <React.Fragment key={row.key}>
                {header && <div className="vg-cmdk-section">{header}</div>}
                <button
                  type="button"
                  id={`vg-cmdk-row-${index}`}
                  role="option"
                  aria-selected={index === active}
                  tabIndex={-1}
                  className={`vg-cmdk-row${index === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => activate(row)}
                >
                  {row.icon && <row.icon size={16} strokeWidth={1.8} aria-hidden="true" />}
                  <span className="vg-cmdk-row-main">
                    <span className="vg-cmdk-row-title">{row.title}</span>
                    {row.subtitle && <span className="vg-cmdk-row-sub">{row.subtitle}</span>}
                  </span>
                  {row.status && <span className="vg-cmdk-row-status">{row.status}</span>}
                </button>
              </React.Fragment>
            );
          })}
          {data.status === 'loading' && (
            <div className="vg-cmdk-loading" aria-label="Datensuche läuft">
              <Skeleton h={12} w="62%" /><Skeleton h={12} w="48%" /><Skeleton h={12} w="55%" />
            </div>
          )}
          {data.status === 'error' && (
            <div style={{ padding: '8px 16px' }}>
              <ErrorBox text="Datensuche derzeit nicht erreichbar." onRetry={() => setRetryTick((t) => t + 1)} />
            </div>
          )}
          {showEmpty && (
            <Empty
              icon={SearchX}
              title="Keine Treffer"
              hint={trimmed ? 'Anderen Begriff versuchen — oder unten in den Fahrten suchen.' : 'Tippen, um Bereiche und Daten zu durchsuchen.'}
              style={{ padding: '26px 20px' }}
            />
          )}
        </div>
        <div className="vg-cmdk-foot">
          <span><kbd>↑</kbd> <kbd>↓</kbd> Navigieren</span>
          <span><kbd>↵</kbd> Öffnen</span>
          <span><kbd>esc</kbd> Schließen</span>
          {trimmed.length === 1 && <span className="vg-cmdk-foot-note">Datensuche ab 2 Zeichen</span>}
        </div>
      </div>
    </div>
  );
}
